<template>
  <div class="autobookpro-page-content quote-details">
    <div class="row items-start q-mb-lg">
      <div class="col-lg-3 col-md-4 col-sm-12">
        <div class="text-h5 row items-center">
          <template v-if="!quoteId">
            <q-icon name="card_travel" />
            <span class="q-ml-sm" v-if="isQuoteContext">Quote ID:&nbsp;</span>
            <span class="q-ml-sm" v-if="isOrderContext">Order ID:&nbsp;</span>
            <span class="text-bold"> #{{ quoteIdValue }}</span>
          </template>
          <q-icon
            class="q-px-sm text-primary"
            v-for="(QUOTE_ENGAGEMENT, index) in QUOTE_ENGAGEMENTS.filter(
              (qe) =>
                qe.position === 'quote_header' &&
                qe.automatic &&
                engagements.includes(qe.value)
            )"
            v-bind:key="`automatic-${index}`"
            :name="QUOTE_ENGAGEMENT.icon"
          />
          <div class="q-pl-xs">
            <slot name="afterTitle"></slot>
          </div>
        </div>
        <div>
          <div v-if="parent">
            <span>Parent Quote</span>
            <a
              class="text-bold text-primary"
              :href="`/quotes/${parent.id}`"
              target="_blank"
              v-if="parent && !parent.isOrder"
            >
              #{{ parent.id }}
            </a>
            <a
              class="text-bold text-primary"
              :href="`/orders/${parent.id}`"
              target="_blank"
              v-if="parent && parent.isOrder"
            >
              #{{ parent.id }}
            </a>
          </div>
          <div v-if="quote && quote.children.length > 0">
            <span>Children</span>
            <a
              class="text-bold text-primary"
              :href="`/${child.isOrder ? 'orders' : 'quotes'}/${child.id}`"
              target="_blank"
              v-for="child in quote.children"
              v-bind:key="child.id"
            >
              #{{ child.id }}
            </a>
          </div>
        </div>
      </div>
      <div class="col-lg-9 col-md-8 col-sm-12 row justify-end q-gutter-sm">
        <q-btn
          v-if="canUpdateQuote"
          color="primary"
          label="Update"
          @click="handleUpdate"
          :loading="loadingSave"
          :disable="loading"
        />
        <q-btn
          v-if="
            this.isOrderContext &&
            canDispatch &&
            quote &&
            (!quote.parentStatus ||
              quote.parentStatus.id !== DISPATCHED_STATUS_ID)
          "
          color="primary"
          label="Dispatch"
          @click="$refs.dispatchToCarrierDialogRef.openDialog()"
          :disable="loading"
        />
        <q-btn-dropdown
          v-if="canContactShipper"
          color="primary"
          label="Contact Shipper"
          :disable="loading"
        >
          <q-list>
            <q-item clickable>
              <q-item-section>Send Email</q-item-section>
              <q-item-section side>
                <q-icon name="keyboard_arrow_right" />
              </q-item-section>
              <q-menu anchor="top right" self="top left">
                <q-list>
                  <q-item
                    clickable
                    v-for="template in emailTemplates"
                    :key="template.id"
                    v-close-popup
                    @click="handleSendEmail(template)"
                    :disable="!shipper.email"
                  >
                    <q-item-section>{{ template.name }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-item>
            <q-item clickable>
              <q-item-section>Send SMS</q-item-section>
              <q-item-section side>
                <q-icon name="keyboard_arrow_right" />
              </q-item-section>
              <q-menu anchor="top right" self="top left">
                <q-list>
                  <q-item
                    clickable
                    v-for="template in smsTemplates"
                    :key="template.id"
                    v-close-popup
                    @click="handleSendSMS(template)"
                  >
                    <q-item-section>{{ template.name }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-item>
            <q-item clickable>
              <q-item-section>Send VM</q-item-section>
              <q-item-section side>
                <q-icon name="keyboard_arrow_right" />
              </q-item-section>
              <q-menu anchor="top right" self="top left">
                <q-list>
                  <q-item
                    clickable
                    v-for="file in vmAudioFiles"
                    :key="file.name"
                    v-close-popup
                    @click="handleSendVM(file)"
                  >
                    <q-item-section>{{ file.name }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-item>
            <q-item
              clickable
              v-close-popup
              @click="handleSendTosEmail"
              v-if="isOrderContext"
            >
              <q-item-section>Email Terms & Conditions</q-item-section>
            </q-item>
            <q-item
              clickable
              v-close-popup
              @click="handleSendBillingInfoEmail"
              v-if="isOrderContext"
            >
              <q-item-section>Email Billing Info Input</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
        <q-btn-dropdown
          v-if="canCreateQuote"
          color="primary"
          label="Copy"
          :loading="duplicating"
          :disable="loading"
        >
          <q-list>
            <q-item
              clickable
              v-close-popup
              @click="handleDuplicate('STANDARD')"
            >
              <q-item-section>
                <q-item-label>Standard</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="handleDuplicate('REVERSE')">
              <q-item-section>
                <q-item-label>Reverse</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
        <q-btn
          v-if="canBookOrder"
          color="secondary"
          label="Book Order"
          @click="handleConvert(true)"
          :disable="loading"
        />
        <q-btn
          v-if="canConvertToQuote"
          color="secondary"
          label="Convert to Quote"
          @click="handleConvert(false)"
          :disable="loading"
        />
        <q-btn
          color="orange"
          label="Find Similar Loads on Central Dispatch"
          type="a"
          v-if="origin.city"
          :href="centralDispatchSearch"
          target="_blank"
          :disable="loading"
        />
      </div>
    </div>

    <q-separator class="q-my-sm" v-if="!quoteId" />

    <div class="q-mb-md row justify-between select-filters">
      <div class="row q-gutter-x-sm">
        <app-select-input
          :loadingData="loading"
          :readonly="!canUpdateQuote && !canNotUpdateButPostToLoadBoardOnly"
          style="width: 250px"
          use-input
          label="Status"
          label-color="white"
          bg-color="primary"
          filled
          :input-style="{ color: '#fff' }"
          v-model="parentStatus"
          :options="parentStatusOptions"
          option-label="name"
          option-value="id"
          :loading="loadingParentStatus"
          @filter="filterParentStatusFn"
          @input="handleParentStatusChange"
        />
        <app-select-input
          :loadingData="loading"
          :readonly="!canUpdateQuote"
          style="min-width: 250px"
          class="q-mx-sm"
          use-input
          label="Child Status"
          label-color="white"
          bg-color="primary"
          filled
          :input-style="{ color: '#fff' }"
          v-model="childStatus"
          :options="childStatusOptions"
          option-label="name"
          option-value="id"
          :loading="loadingChildStatus"
          @filter="filterChildStatusFn"
          @input="dirty.childStatus = true"
        />
      </div>
      <div class="row q-gutter-x-sm">
        <app-customer-messaging-options
          :loadingData="loading"
          :readonly="!canUpdateQuote"
          v-model="shipper"
          @input="dirty.shipper = true"
        />
      </div>

      <div class="row q-gutter-x-sm">
        <app-select-input
          :loadingData="loading"
          :readonly="!canUpdateQuote"
          style="min-width: 250px"
          v-model="referrerId"
          label="Referrer"
          label-color="white"
          bg-color="primary"
          filled
          option-value="id"
          option-label="name"
          :options="referrers"
          :disable="loadingSave"
          @input="dirty.referrer = true"
          emit-value
          map-options
        />
        <app-select-input
          :loadingData="loading"
          :readonly="!canUpdateQuote"
          style="min-width: 250px"
          v-model="assignee"
          label="Assignee"
          label-color="white"
          bg-color="primary"
          filled
          :options="assignees"
          option-value="id"
          :option-label="(item) => `${item.firstName} ${item.lastName}`"
          :disable="loadingSave"
          @input="dirty.assignee = true"
        />
      </div>
    </div>

    <div class="q-mb-md row q-col-gutter-sm">
      <div class="col-md-8 col-sm-12 q-col-gutter-y-sm">
        <div class="row q-col-gutter-sm items-strech">
          <div :class="`${isOrderContext ? 'col-12' : 'col'}`">
            <app-customer-info-card
              :loadingData="loading"
              :possibleDuplicates="possibleDuplicates"
              :context="`${isQuoteContext ? 'quote' : 'order'}`"
              :readonly="!canUpdateQuote"
              action="edit"
              v-model="shipper"
              :validation="$v.shipper"
              @input="dirty.shipper = true"
            >
              <template #header>
                <q-btn
                  v-for="(QUOTE_ENGAGEMENT, index) in QUOTE_ENGAGEMENTS.filter(
                    (qe) => qe.position !== 'pricing_header' && !qe.automatic
                  )"
                  v-bind:key="`nonautomatic-${index}`"
                  size="sm"
                  flat
                  :color="`${
                    engagements.includes(QUOTE_ENGAGEMENT.value)
                      ? 'negative'
                      : ''
                  }`"
                  :icon="QUOTE_ENGAGEMENT.icon"
                  round
                  @click="handleSetEngagement(QUOTE_ENGAGEMENT.value)"
                  :disable="!canUpdateQuote || loading"
                />
              </template>
            </app-customer-info-card>
          </div>
          <div class="col" v-if="isQuoteContext">
            <app-follow-up-card
              :loadingData="loading"
              :context="`${isQuoteContext ? 'quote' : 'order'}`"
              :readonly="!canUpdateQuote"
              action="edit"
              v-model="followups"
              @input="dirty.followups = true"
              style="height: 100%"
            />
          </div>
        </div>
        <div class="row q-col-gutter-sm items-strech">
          <div class="col">
            <app-carrier-note-input
              :loadingData="loading"
              :readonly="!canUpdateQuote"
              action="edit"
              v-model="shipper"
              @input="dirty.shipper = true"
            />
          </div>
          <div class="col">
            <app-quote-report-card
              style="height: 100%"
              :basePriceText="basePriceText"
              :eventToReport="eventToReport"
            />
          </div>
          <div class="col-6" v-if="quote && quote.dispatch">
            <app-order-dispatch-info :dispatch="quote.dispatch" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <app-map-box-map-card ref="mapCardRef" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <app-vehicle-details-card
              :loadingData="loading"
              :context="`${isQuoteContext ? 'quote' : 'order'}`"
              :readonly="!canUpdateQuote"
              action="edit"
              v-model="vehicles"
              :validation="$v.vehicles"
              @input="dirty.vehicles = true"
            />
          </div>
        </div>
        <div class="row q-col-gutter-sm">
          <div class="col">
            <app-location-filterable-card
              :loadingData="loading"
              :context="`${isQuoteContext ? 'quote' : 'order'}`"
              :readonly="!canUpdateQuote"
              action="edit"
              label="Origin"
              v-model="origin"
              :validation="$v.origin"
              @input="
                (payload, name) => handleLocationChange('origin', payload, name)
              "
            >
              <template #header>
                <q-btn
                  icon="person"
                  round
                  flat
                  size="sm"
                  class="q-icon-btn"
                  @click="assignLocationPerson('origin')"
                />
              </template>
            </app-location-filterable-card>
          </div>
          <div class="col">
            <app-location-filterable-card
              :loadingData="loading"
              :context="`${isQuoteContext ? 'quote' : 'order'}`"
              :readonly="!canUpdateQuote"
              action="edit"
              label="Destination"
              v-model="destination"
              :validation="$v.destination"
              @input="
                (payload, name) =>
                  handleLocationChange('destination', payload, name)
              "
            >
              <template #header>
                <q-btn
                  icon="person"
                  round
                  flat
                  size="sm"
                  class="q-icon-btn"
                  @click="assignLocationPerson('destination')"
                />
              </template>
            </app-location-filterable-card>
          </div>
        </div>
      </div>
      <div class="col-md-4 col-sm-12 q-col-gutter-y-sm">
        <div>
          <app-transport-pricing-card
            :loadingData="loading"
            :context="`${isQuoteContext ? 'quote' : 'order'}`"
            :readonly="!canUpdateQuote"
            v-model="transport"
            :fullPayEnabled="
              (engagements).includes(QUOTE_ENGAGEMENT_FULLPAY)
            "
            @run-taq="handleRunTAQ"
            :running-taq="runningTAQ"
            :validation="$v.transport"
            @input="dirty.transport = true"
          >
            <template #header>
              <q-btn
                v-for="(QUOTE_ENGAGEMENT, index) in QUOTE_ENGAGEMENTS.filter(
                  (qe) => qe.position === 'pricing_header' && !qe.automatic
                )"
                v-bind:key="`nonautomatic-${index}`"
                size="sm"
                flat
                :color="`${
                  engagements.includes(QUOTE_ENGAGEMENT.value) ? 'negative' : ''
                }`"
                :icon="QUOTE_ENGAGEMENT.icon"
                round
                @click="handleSetEngagement(QUOTE_ENGAGEMENT.value)"
                :disable="!canUpdateQuote || loading"
              />
            </template>
          </app-transport-pricing-card>
        </div>
        <div>
          <app-transport-info-card
            ref="transportInfoCardRef"
            :loadingData="loading"
            :context="`${isQuoteContext ? 'quote' : 'order'}`"
            :readonly="!canUpdateQuote"
            action="edit"
            v-model="transport"
            :validation="$v.transport"
            @input="dirty.transport = true"
          />
        </div>

        <div>
          <app-internal-notes-card
            :loadingData="loading"
            :context="`${isQuoteContext ? 'quote' : 'order'}`"
            :readonly="!canUpdateQuote"
            action="edit"
            title="Notes"
            v-model="internalNotes"
            @input="saveInternalNotes"
          />
        </div>

        <div v-if="isOrderContext">
          <app-payment-history-card
            ref="paymentHistoryCardRef"
            :loadingData="loading"
            :shipper="shipper"
            :origin="origin"
            :destination="destination"
            :transactions="transactions"
            :billingInfo="billingInfo"
            :saving="savingBillingInfo"
            :charging="chargingCustomer"
            @on-change-billing-info="handleChangeBillingInfo"
            @on-charge-order="handleChargeCustomer"
          >
            <template #header>
              <q-icon
                v-if="engagements.includes(QUOTE_ENGAGEMENT_FULLPAY)"
                class="text-primary"
                :name="
                  QUOTE_ENGAGEMENTS.find(
                    (e) => e.value === QUOTE_ENGAGEMENT_FULLPAY
                  ).icon
                "
              />
            </template>
          </app-payment-history-card>
        </div>

        <div v-if="isOrderContext">
          <app-terms-and-condition-card
            :loadingData="loading"
            context="order"
            :value="terms"
          />
        </div>
        <!-- <div>
          <app-doc-image-uploader-card :data="quote.id" />
        </div> -->
      </div>
    </div>

    <div class="q-mb-md row q-col-gutter-sm">
      <app-quote-history-card
        class="col-6"
        label="Email History"
        :loadingData="loadingEvents"
        :events="quoteEvents"
        eventType="EMAIL"
      ></app-quote-history-card>

      <app-quote-history-card
        class="col-6"
        label="SMS History"
        :loadingData="loadingEvents"
        :events="quoteEvents"
        eventType="SMS"
      ></app-quote-history-card>
    </div>

    <app-quote-history-card
      label="History"
      :loadingData="loadingEvents"
      :events="quoteEvents"
    ></app-quote-history-card>

    <app-dispatch-to-carrier-dialog
      ref="dispatchToCarrierDialogRef"
      :orderId="this.theQuoteId"
    />
  </div>
</template>
<style lang="scss">
.quote-details .select-filters {
  .q-field__marginal,
  .q-field__native {
    color: #fff;
  }
}
.q-icon-btn .q-icon {
  font-size: 2.4em;
}
.quote-details-page .q-menu .q-virtual-scroll__content > div:nth-child(odd) {
  background-color: #eee !important;
}
</style>
<style scoped lang="scss">
.q-list > div:nth-child(odd) {
  background-color: #eee !important;
}
</style>
<script>
import { quoteValidator, orderValidator } from '@client/validators';
import graphql from '@client/graphql';
import {
  extractKeysIntoObject,
  sanitizeObject
} from '@client/utils/object-helpers';
import { QUOTE_ENGAGEMENTS, QUOTE_ENGAGEMENT_FULLPAY } from '@server/constants';
import { assessPermission } from '@server/lib/permission-helpers';
import AppQuoteHistoryCard from '@client/components/cards/quote-history-card';
import AppCustomerInfoCard from '@client/components/cards/customer-info-card';
import AppCustomerMessagingOptions from '@client/components/cards/customer-messaging-options';
import AppLocationFilterableCard from '@client/components/cards/location-filterable-card';
import AppVehicleDetailsCard from '@client/components/cards/vehicle-details-card';
import AppTransportInfoCard from '@client/components/cards/transport-info-card';
import AppTransportPricingCard from '@client/components/cards/transport-pricing-card';
import AppInternalNotesCard from '@client/components/cards/internal-notes-card';
import AppFollowUpCard from '@client/components/cards/follow-up-card';
import AppPaymentHistoryCard from '@client/components/cards/payment-history-card';
import AppTermsAndConditionCard from '@client/components/cards/terms-and-condition-card';
import AppSelectInput from '@client/components/form/select-input';
import AppDispatchToCarrierDialog from '@client/components/dialogs/dispatch-to-carrier-dialog';
import AppMapBoxMapCard from '@client/components/cards/mapbox/mapbox-map-card';
import AppOrderDispatchInfo from '@client/components/cards/dispatch-info';
import AppQuoteReportCard from '@client/components/cards/quote-report-card';
import AppCarrierNoteInput from '@client/components/cards/carrier-note-input';
import { debounce } from 'quasar';
import { rrFormatPhoneNumber } from '@client/utils/app-helpers';
// import AppDocImageUploaderCard from '@client/components/cards/doc-image-uploader-card';

export default {
  name: 'AppQuoteDetail',
  metaInfo() {
    let title = 'Quote';
    if (this.isOrderContext) title = 'Order';

    return {
      title: !this.quote
        ? 'Quote Page'
        : `${this.quote.shipper.firstName} ${this.quote.shipper.lastName}'s ${title}`
    };
  },
  graphql,
  components: {
    AppCustomerInfoCard,
    AppCustomerMessagingOptions,
    AppLocationFilterableCard,
    AppVehicleDetailsCard,
    AppTransportInfoCard,
    AppTransportPricingCard,
    AppInternalNotesCard,
    AppFollowUpCard,
    AppPaymentHistoryCard,
    AppSelectInput,
    AppTermsAndConditionCard,
    AppDispatchToCarrierDialog,
    AppMapBoxMapCard,
    AppQuoteHistoryCard,
    AppOrderDispatchInfo,
    AppQuoteReportCard,
    AppCarrierNoteInput
  },
  props: {
    quoteId: String,
    quoteType: {
      type: String,
      default: 'quote'
    }
  },
  validations() {
    if (this.isQuoteContext) return quoteValidator;
    return orderValidator;
  },
  apollo: {
    currentUser: {
      query: graphql.queries.currentUser
    },
    quote() {
      let gql = graphql.queries.getQuote;
      if (this.isOrderContext) gql = graphql.queries.getOrder;

      return {
        query: gql,
        variables() {
          return { id: this.theQuoteId };
        },
        watchLoading(isLoading) {
          this.loading = isLoading;
        },
        error(error) {
          this.error = error;
        },
        update(data) {
          if (this.isQuoteContext) return data.quote;
          return data.order;
        },
        async result(result) {
          if (result.loading) return;

          let theQuote = result.data.quote;
          if (this.isOrderContext) theQuote = result.data.order;
          Object.assign(this, theQuote);

          this.engagements = theQuote.engagements || [];
          const response = await this.$apollo.query({
            query: graphql.queries.findPossibleDuplicates,
            variables: { id: this.theQuoteId },
            fetchPolicy: 'network-only'
          });

          this.possibleDuplicates = response.data.findPossibleDuplicates;
          this.$refs.mapCardRef.refreshMap({
            origin: this.origin,
            destination: this.destination
          });
        },
        errorPolicy: 'all'
      };
    },
    quoteEvents: {
      query: graphql.queries.getQuoteEvents,
      variables() {
        return { id: this.theQuoteId };
      },
      watchLoading(isLoading) {
        this.loadingEvents = isLoading;
      }
    },
    parentStatuses: {
      query: graphql.queries.getParentStatus,
      variables() {
        if (this.isOrderContext) return { type: 'ORDER' };
        return { type: 'QUOTE' };
      },
      watchLoading(isLoading) {
        this.loadingParentStatus = isLoading;
      }
    },
    emailTemplates: {
      query: graphql.queries.getEmailTemplates
    },
    smsTemplates: {
      query: graphql.queries.getSMSTemplates
    },
    vmAudioFiles: {
      query: graphql.queries.getVMAudioFiles
    },
    assignees: {
      query: graphql.queries.loadAssignees
    },
    referrers: {
      query: graphql.queries.getReferrers
    },
    basePriceFactors: {
      query: graphql.queries.getBasePriceFactors,
      variables() {
        return {
          origin: sanitizeObject(this.origin),
          destination: sanitizeObject(this.destination)
        };
      },
      skip() {
        return !this.origin?.zipcode;
      }
    }
  },
  watch: {
    $route(to, from) {
      this.isEditingQuote = from.query.edit === 'true';
    },
    'origin.zipcode': function () {
      this.handleZipcodeUpdate('origin');
    },
    'destination.zipcode': function () {
      this.handleZipcodeUpdate('destination');
    }
  },
  created() {
    this.handleLocationChange = debounce(this.handleLocationChange, 500);
    this.isEditingQuote = this.$route.query.edit === 'true';
    document.body.classList.add('quote-details-page');
    // this.handleRunTAQ(true);
  },
  destroyed() {
    document.body.classList.remove('quote-details-page');
  },
  // beforeMount() {
  //   const preventNav = (event) => {
  //     if (Object.keys(this.dirty).length > 0) {
  //       event.preventDefault();
  //       // eslint-disable-next-line no-param-reassign
  //       event.returnValue = '';
  //     }
  //   };

  //   window.addEventListener('beforeunload', preventNav);
  //   this.$once('hook:beforeDestroy', () => {
  //     window.removeEventListener('beforeunload', preventNav);
  //   });
  // },
  // beforeRouteLeave(to, from, next) {
  //   if (Object.keys(this.dirty).length > 0) {
  //     this.$q
  //       .dialog({
  //         title: 'Confirm',
  //         message:
  //           'Changes you made may not be saved. Are you sure you want to leave?',
  //         cancel: true,
  //         persistent: true
  //       })
  //       .onOk(() => {
  //         next();
  //       })
  //       .onCancel(() => {
  //         next(false);
  //       });
  //   } else {
  //     next();
  //   }
  // },
  data() {
    return {
      mapLoaded: {
        origin: false,
        destination: false
      },
      possibleDuplicates: {
        byEmailPhone: [],
        byName: []
      },

      isEditingQuote: false,

      loading: false,
      error: null,
      id: null,

      parent: null,
      assignee: null,

      loadingParentStatus: false,
      parentStatus: null,
      parentStatusOptions: [],

      loadingChildStatus: false,
      childStatus: null,
      childStatusOptions: [],

      loadingEvents: false,

      shipper: {},
      origin: {},
      destination: {},
      vehicles: [
        {
          year: null,
          make: null,
          model: null,
          size: {
            id: null,
            name: ''
          }
        }
      ],

      runningTAQ: false,
      transport: {},

      internalNotes: [],
      followups: [],

      terms: null,
      billingInfo: null,
      transactions: [],

      engagements: [],
      QUOTE_ENGAGEMENTS,
      QUOTE_ENGAGEMENT_FULLPAY,

      dispatch: null,

      loadingSave: false,
      savingBillingInfo: false,
      chargingCustomer: false,

      dirty: {},

      duplicating: false,

      DISPATCHED_STATUS_ID: window.DISPATCHED_STATUS_ID,

      referrerId: null,
      isFromProabd: false,
      proabdExternalId: ''
    };
  },
  computed: {
    theQuoteId() {
      return this.$route.params.id || this.quoteId;
    },
    quoteIdValue() {
      return this.isFromProabd ? `LD${this.proabdExternalId}` : this.id;
    },
    eventToReport() {
      if (this.quoteEvents) {
        return this.quoteEvents
          .slice()
          .reverse()
          .find((q) => q.eventType === 'GENERAL');
      }
      return null;
    },
    isQuoteContext() {
      if (this.quoteId) return this.quoteType === 'quote';
      return this.$route.name === 'quote-detail';
    },
    isOrderContext() {
      if (this.quoteId) return this.quoteType === 'order';
      return this.$route.name === 'order-detail';
    },
    canContactShipper() {
      if (
        this.isQuoteContext &&
        assessPermission.quote.canContactShipper(this.currentUser)
      ) {
        return true;
      }

      return (
        this.isOrderContext &&
        assessPermission.order.canContactShipper(this.currentUser)
      );
    },
    canCreateQuote() {
      if (
        this.isQuoteContext &&
        assessPermission.quote.canCreate(this.currentUser)
      ) {
        return true;
      }

      return (
        this.isOrderContext &&
        assessPermission.order.canCreate(this.currentUser)
      );
    },
    canBookOrder() {
      return (
        this.isQuoteContext &&
        assessPermission.order.canCreate(this.currentUser)
      );
    },
    canConvertToQuote() {
      return (
        this.isOrderContext &&
        assessPermission.quote.canCreate(this.currentUser)
      );
    },
    canUpdateQuote() {
      if (
        this.isQuoteContext &&
        assessPermission.quote.canUpdate(this.currentUser)
      ) {
        return true;
      }

      return (
        this.isOrderContext &&
        assessPermission.order.canUpdate(this.currentUser)
      );
    },
    canDispatch() {
      return (
        this.isOrderContext &&
        assessPermission.order.canDispatchOnly(this.currentUser)
      );
    },
    canUpdateButNotPostToLoadBoardOnly() {
      return (
        this.isOrderContext &&
        assessPermission.order.canUpdate(this.currentUser) &&
        !assessPermission.order.canPostToLoadBoardOnly(this.currentUser)
      );
    },
    canNotUpdateButPostToLoadBoardOnly() {
      return (
        this.isOrderContext &&
        !assessPermission.order.canUpdate(this.currentUser) &&
        assessPermission.order.canPostToLoadBoardOnly(this.currentUser)
      );
    },
    basePriceText() {
      const priceType = this.basePriceFactors?.type;
      const data = this.basePriceFactors?.data;
      const price = data?.price;
      let priceInfo = '';
      let t = '';
      if (priceType === 'MILEAGE') {
        t = `Mileage Pricing : $${price}`;
        priceInfo = `${data?.min}mi - ${data?.max}mi`;
      } else {
        t = `Fixed Lane Price : ${price}`;
        priceInfo = `${data?.originCity} ${data?.originState} > ${data?.destCity} ${data?.destState}`;
      }
      return `${t} - ${priceInfo}`;
    },
    centralDispatchSearch() {
      const origin = `s~%27Pickup~r~50~c~%27${this.origin.city}~st~%27${this.origin.state}`;
      const destination = `s~%27Dropoff~r~50~c~%27${this.destination.city}~st~%27${this.destination.state}`;

      const baseUrl =
        'https://app.centraldispatch.com/search?search=~(vC~(mn~1)~tLPW~2~opt~%27all~ofst~0~lmt~50~sF~(~(n~%27pup~d~%27ASC)~(n~%27dMA~d~%27ASC))~dBS~false~sPSO~false~sTOT~false~rT~%27Open';
      return `${baseUrl}~l~(~(${origin})~(${destination})))`;
    }
  },
  methods: {
    assignLocationPerson(modelName) {
      const model = this[modelName];
      model.name = this.shipper.firstName;
      model.phone =
        this.shipper.phone.substring(0, 2) === '+1'
          ? this.shipper.phone.substring(2)
          : this.shipper.phone;
      model.phone2 =
        this.shipper.phone2.substring(0, 2) === '+1'
          ? this.shipper.phone2.substring(2)
          : this.shipper.phone2;
    },
    async saveInternalNotes() {
      this.loadingSave = true;
      const inputData = extractKeysIntoObject(this, ['internalNotes']);
      const payload = {
        internalNotes: inputData.internalNotes.map(sanitizeObject)
      };
      if (Object.keys(payload).length > 0) {
        await this.updateFields(payload);
      }
      this.loadingSave = false;
    },
    async handleSetEngagement(engagement) {
      if (this.engagements.includes(engagement)) {
        this.engagements = this.engagements.filter((e) => e !== engagement);
      } else {
        this.engagements = this.engagements.concat(engagement);
      }

      this.dirty.engagements = true;
    },
    filterParentStatusFn(val, update) {
      update(() => {
        const needle = val.toLowerCase();
        this.parentStatusOptions = this.parentStatuses
          .filter((v) => v.name.toLowerCase().indexOf(needle) > -1)
          .map((status) => {
            if (this.canUpdateButNotPostToLoadBoardOnly) {
              return {
                ...status,
                disable: status.id === window.POST_TO_LOAD_BOARD_ID
              };
            }
            if (this.canNotUpdateButPostToLoadBoardOnly) {
              return {
                ...status,
                disable: status.id !== window.POST_TO_LOAD_BOARD_ID
              };
            }
            return status;
          });
      });
    },
    async handleParentStatusChange(value) {
      this.dirty.parentStatus = true;
      if (value.childrenCount > 0) {
        this.childStatusOptions = value.children;
      } else {
        this.childStatusOptions = [];
      }

      this.childStatus = null;
    },
    filterChildStatusFn(val, update) {
      update(() => {
        const needle = val.toLowerCase();
        const parentStatus = this.parentStatuses.find(
          (s) => s.id === this.parentStatus.id
        );
        this.childStatusOptions = parentStatus
          ? parentStatus.children.filter(
              (v) => v.name.toLowerCase().indexOf(needle) > -1
            )
          : [];
      });
    },
    handleLocationChange(key, payload, name) {
      this.dirty[key] = true;
      if (!this.origin.zipcode || !this.destination.zipcode) return;
      if (['address', 'city', 'state', 'zipcode'].includes(name)) {
        this.$refs.mapCardRef.refreshMap({
          origin: this.origin,
          destination: this.destination
        });
      }
    },
    handleZipcodeUpdate(key) {
      this.$refs.transportInfoCardRef.calcDistance(
        this.origin.zipcode,
        this.destination.zipcode
      );

      if (
        !this.mapLoaded[key] &&
        this.origin.zipcode &&
        this.destination.zipcode
      ) {
        this.$refs.mapCardRef.refreshMap({
          origin: this.origin,
          destination: this.destination
        });
      }
      // handleLocationChange will take care of refreshing the map onwards
      if (!this.mapLoaded[key]) this.mapLoaded[key] = true;
    },
    async updateFields(data) {
      let gql = graphql.mutations.updateQuote;
      if (this.isOrderContext) gql = graphql.mutations.updateOrder;

      try {
        const response = await this.$apollo.mutate({
          mutation: gql,
          variables: {
            id: this.theQuoteId,
            input: data
          },
          update: () => {
            this.$apollo.queries.quoteEvents.refetch();
          }
        });

        this.dirty = {};

        return response;
      } catch (error) {
        this.notifyNegative(error.message);
        return error;
      }
    },
    async handleUpdate() {
      this.validate([
        'shipper',
        'origin',
        'destination',
        'transport',
        'vehicles'
      ]);

      this.loadingSave = true;

      const changed = [];
      if (this.dirty.engagements) changed.push('engagements');
      if (this.dirty.referrer) changed.push('referrerId');
      if (this.dirty.assignee) changed.push('assignee');
      if (this.dirty.parentStatus) changed.push('parentStatus');
      if (this.dirty.childStatus) changed.push('childStatus');
      if (this.dirty.origin) changed.push('origin');
      if (this.dirty.destination) changed.push('destination');
      if (this.dirty.shipper) changed.push('shipper');
      if (this.isQuoteContext && this.dirty.followups) {
        changed.push('followups');
      }
      if (this.dirty.vehicles) changed.push('vehicles');
      if (this.dirty.transport) changed.push('transport');
      const inputData = extractKeysIntoObject(this, changed);

      const payload = {};

      if (changed.includes('shipper')) {
        payload.shipper = sanitizeObject(inputData.shipper);
        payload.shipper.note = inputData.shipper.note;
        payload.shipper.phone = rrFormatPhoneNumber(payload.shipper.phone);
        payload.shipper.phone2 = rrFormatPhoneNumber(payload.shipper.phone2);
      }
      if (changed.includes('origin')) {
        payload.origin = sanitizeObject(inputData.origin);
        payload.origin.phone = rrFormatPhoneNumber(payload.origin.phone);
        payload.origin.phone2 = rrFormatPhoneNumber(payload.origin.phone2);
      }
      if (changed.includes('destination')) {
        payload.destination = sanitizeObject(inputData.destination);
        payload.destination.phone = rrFormatPhoneNumber(
          payload.destination.phone
        );
        payload.destination.phone2 = rrFormatPhoneNumber(
          payload.destination.phone2
        );
      }
      if (changed.includes('transport')) {
        const transportData = sanitizeObject(inputData.transport);
        payload.transport = {
          carrierType: transportData.carrierType,
          availableDate: transportData.availableDate,
          deliveryDate: transportData.deliveryDate,
          basePrice: transportData.basePrice,
          carrierPay: transportData.carrierPay,
          totalPrice: transportData.totalPrice,
          fullPay: transportData.fullPay,
          sizeFee: transportData.sizeFee,
          inopFee: transportData.inopFee,
          enclosedFee: transportData.enclosedFee,
          deposit: transportData.deposit,
          sizeFeeByDistance: transportData.sizeFeeByDistance,
          depositFeeByDistance: transportData.depositFeeByDistance
        };
      }
      if (changed.includes('vehicles')) {
        payload.vehicles = inputData.vehicles
          .map(sanitizeObject)
          .map(({ size, ...v }) => {
            if (size) return { ...v, sizeId: size.id };
            return v;
          });
      }
      if (changed.includes('assignee')) {
        payload.assigneeId = parseInt(inputData.assignee.id, 10);
      }
      if (changed.includes('referrerId')) {
        payload.referrerId = Number(inputData.referrerId);
      }
      if (changed.includes('parentStatus')) {
        payload.parentStatusId = inputData.parentStatus
          ? parseInt(inputData.parentStatus.id, 10)
          : null;
      }
      if (changed.includes('childStatus')) {
        payload.childStatusId = inputData.childStatus
          ? parseInt(inputData.childStatus.id, 10)
          : null;
      }
      if (changed.includes('followups')) {
        payload.followups = inputData.followups.map((e) => {
          const followup = this.quote.followups.find(
            (f) => f.followupOn === e.followupOn
          );
          return followup ? sanitizeObject(followup) : e;
        });
      }
      if (changed.includes('engagements')) {
        payload.engagements = inputData.engagements;
      }

      if (Object.keys(payload).length > 0) {
        await this.updateFields(payload);
      }

      this.loadingSave = false;
    },
    async handleDuplicate(mode) {
      this.duplicating = true;

      let gql = graphql.mutations.duplicateQuote;
      if (this.isOrderContext) gql = graphql.mutations.duplicateOrder;

      const data = await this.$apollo.mutate({
        mutation: gql,
        variables: {
          id: this.theQuoteId,
          mode
        }
      });

      this.duplicating = false;

      if (this.isQuoteContext) {
        const routerData = this.$router.resolve({
          name: 'quote-detail',
          params: { id: data.data.duplicateQuote.id },
          query: assessPermission.quote.canUpdate(this.currentUser)
            ? { edit: 'true' }
            : {}
        });
        window.open(routerData.href, '__blank');
      }

      if (this.isOrderContext) {
        const routerData = this.$router.resolve({
          name: 'order-detail',
          params: { id: data.data.duplicateOrder.id },
          query: assessPermission.quote.canUpdate(this.currentUser)
            ? { edit: 'true' }
            : {}
        });
        window.open(routerData.href, '__blank');
      }

      this.notifyPositive('The quote has been successfully duplicated');
    },
    async handleSendEmail(template) {
      await this.$apollo.mutate({
        mutation: graphql.mutations.sendQuoteEmail,
        variables: {
          id: this.theQuoteId,
          emailTemplateId: template.id
        },
        update: () => {
          this.$apollo.queries.quoteEvents.refetch();
        }
      });
    },
    async handleSendSMS(template) {
      await this.$apollo.mutate({
        mutation: graphql.mutations.sendQuoteSMS,
        variables: {
          id: this.theQuoteId,
          smsTemplateId: template.id
        },
        update: () => {
          this.$apollo.queries.quoteEvents.refetch();
        }
      });
    },
    async handleSendVM(file) {
      await this.$apollo.mutate({
        mutation: graphql.mutations.sendQuoteVM,
        variables: {
          id: this.theQuoteId,
          name: file.name
        },
        update: () => {
          this.$apollo.queries.quoteEvents.refetch();
        }
      });
    },
    async handleRunTAQ(silent = false) {
      if (silent) {
        console.log('before');
        if (!this.rrIsInvalid(['origin', 'destination', 'vehicles'])) return;
        console.log('after');
      } else {
        this.validate(['origin', 'destination', 'vehicles']);
      }

      this.runningTAQ = true;

      try {
        const response = await this.$apollo.mutate({
          mutation: graphql.mutations.runTransportAutoQuoter,
          variables: {
            origin: this.origin,
            destination: this.destination,
            vehicles: this.vehicles,
            carrierType: this.transport.carrierType,
            referrerId: this.referrerId ? this.referrerId : null
          }
        });
        const { runTransportAutoQuoter } = response.data;
        this.transport = {
          ...this.transport,
          ...runTransportAutoQuoter,
          carrierPay: runTransportAutoQuoter.carrierPay,
          totalPrice: runTransportAutoQuoter.totalPrice,
          fullPay: runTransportAutoQuoter.fullPay
        };
      } catch (error) {
        this.notifyNegative(error.message);
      }

      this.runningTAQ = false;
      this.dirty.transport = true;
    },
    handleConvert(toOrder) {
      let message = 'Are you sure you want to convert to an order?';
      if (!toOrder) {
        message = 'Are you sure you want to convert to a quote?';
      }

      this.$q
        .dialog({
          title: 'Confirm',
          message,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          this.$router.push({
            name: toOrder ? 'convert-to-order' : 'convert-to-quote',
            params: { id: this.theQuoteId }
          });
        });
    },
    async handleChangeBillingInfo(billingInfo) {
      this.savingBillingInfo = true;

      const response = await this.$apollo.mutate({
        mutation: graphql.mutations.setBillingInfoToOrder,
        variables: {
          orderId: this.theQuoteId,
          input: billingInfo
        },
        errorPolicy: 'all'
      });

      this.savingBillingInfo = false;

      if (response && response.errors) {
        response.errors.forEach((err) => {
          this.notifyNegative(err.message);
        });
      } else {
        this.$refs.paymentHistoryCardRef.closeBillingInformationDialog();
      }
    },
    async handleChargeCustomer(data) {
      this.chargingCustomer = true;

      const response = await this.$apollo.mutate({
        mutation: graphql.mutations.chargeOrder,
        variables: {
          orderId: this.theQuoteId,
          amount: data.amount,
          note: data.note
        },
        errorPolicy: 'all'
      });

      this.chargingCustomer = false;

      if (response && response.errors) {
        response.errors.forEach((err) => {
          this.notifyNegative(err.message);
        });
      }
    },
    async handleSendTosEmail() {
      await this.$apollo.mutate({
        mutation: graphql.mutations.sendTermsAndConditionsEmail,
        variables: { id: this.theQuoteId }
      });
    },
    async handleSendBillingInfoEmail() {
      await this.$apollo.mutate({
        mutation: graphql.mutations.sendBillingInfoInputEmail,
        variables: { id: this.theQuoteId }
      });
    }
  }
};
</script>
